'use client';

import { useCallback, useContext, useEffect } from 'react';
import { FeaturesContext } from '@/app/contexts';
import {
  evidenceCategoryScopeFieldsWithWireTokens,
  getNewActiveFileTypes,
} from './evidence-locker-ui-shared-new';
import { useChatEvidenceLockerNew } from './chat-evidence-locker-context-new';

/**
 * Mirror filter-picker file-type scopes into submit payload fields.
 *
 * CRITICAL: derive scope arrays from ``prev`` inside the updater rather
 * than from the render-time closure. When the parent swaps cases, other
 * queued state updates (parent case-switch effect) reset
 * ``evidenceCategoryScopes`` to ``[]`` before this updater runs, but the
 * closure still holds the prior case's value. Using the closure copy here
 * caused this effect to write the prior case's chip selection onto the
 * empty ``prev``, persisting that wrong value into the new case's
 * localStorage via the parent's persisting ``setScopesAndFiles`` bridge.
 */
export function useSyncEvidenceFileTypeFiltersToScopes() {
  const { scopesAndFiles, setScopesAndFiles } = useChatEvidenceLockerNew();
  const featuresContext = useContext(FeaturesContext);
  const contentCategoriesEnabled = !!featuresContext?.featureFlags?.contentCategories;

  useEffect(() => {
    if (!contentCategoriesEnabled) return;
    setScopesAndFiles((prev) => {
      const includedScopeCats = prev?.evidenceCategoryScopes ?? [];
      const excludedScopeCats = prev?.excludedEvidenceCategoryScopes ?? [];
      const synced = evidenceCategoryScopeFieldsWithWireTokens(
        includedScopeCats,
        excludedScopeCats,
        !!prev.evidenceAllFileTypes,
      );
      if (
        JSON.stringify(prev.contentCategories ?? []) ===
          JSON.stringify(synced.contentCategories) &&
        JSON.stringify(prev.excludedContentCategories ?? []) ===
          JSON.stringify(synced.excludedContentCategories) &&
        JSON.stringify(prev.evidenceCategoryChips ?? []) ===
          JSON.stringify(synced.evidenceCategoryChips) &&
        JSON.stringify(prev.evidenceCategoryScopes ?? []) ===
          JSON.stringify(synced.evidenceCategoryScopes) &&
        JSON.stringify(prev.excludedEvidenceCategoryScopes ?? []) ===
          JSON.stringify(synced.excludedEvidenceCategoryScopes) &&
        prev.evidenceAllFileTypes === synced.evidenceAllFileTypes
      ) {
        return prev;
      }
      return {
        ...prev,
        ...synced,
      };
    });
  }, [
    contentCategoriesEnabled,
    scopesAndFiles.evidenceCategoryScopes,
    scopesAndFiles.excludedEvidenceCategoryScopes,
    setScopesAndFiles,
  ]);
}

export function useEvidenceFileTypeFiltersNew() {
  const {
    showFolders,
    setShowFolders,
    allFileTypes,
    setAllFileTypes,
    activeFileTypes,
    setActiveFileTypes,
    scopesAndFiles,
  } = useChatEvidenceLockerNew();
  const featuresContext = useContext(FeaturesContext);
  const contentCategoriesEnabled = !!featuresContext?.featureFlags?.contentCategories;

  const selectedContentCategories =
    scopesAndFiles.evidenceCategoryScopes ?? [];

  const toggleFileType = useCallback(
    (fileType: string) => {
      const updated = getNewActiveFileTypes(fileType, activeFileTypes);
      if (Object.values(updated).some((v) => v === true)) {
        setAllFileTypes(false);
      }
      setActiveFileTypes(updated);
    },
    [activeFileTypes, setActiveFileTypes, setAllFileTypes],
  );

  const toggleShowFolders = useCallback(() => {
    setShowFolders((v) => !v);
  }, [setShowFolders]);

  const toggleAllFileTypes = useCallback(() => {
    if (!allFileTypes) {
      setActiveFileTypes({});
    }
    setAllFileTypes((v) => !v);
  }, [allFileTypes, setActiveFileTypes, setAllFileTypes]);

  const activeFileTypeChipCount = Object.keys(activeFileTypes).filter(
    (key) => activeFileTypes[key] === true,
  ).length;

  const evidenceLockerCategoryScopeActive =
    contentCategoriesEnabled && !allFileTypes && activeFileTypeChipCount > 0;

  return {
    contentCategoriesEnabled,
    showFolders,
    allFileTypes,
    activeFileTypes,
    selectedContentCategories,
    toggleFileType,
    toggleShowFolders,
    toggleAllFileTypes,
    activeFileTypeChipCount,
    evidenceLockerCategoryScopeActive,
  };
}
